import createMitt from '@livechat/mitt'
import { connectToParent } from '@livechat/postmessage'
import { navigate } from 'gatsby'

import { store } from '@/store'
import { create as fetchCart, saveCart } from '@/store/cart'
import { fetchInstalledApps } from '@/store/installedApps'
import { fetchSubscription } from '@/store/subscription'
import { isInIframe } from '@/utils/browser/iframe'
import { canUseWindow } from '@/utils/browser/window'
import { trailingSlash } from '@/utils/regex'

import { PLUGIN_ID, PM_EVENT } from './constants'
import { ConnectionDriver, InboxMessage } from './types'
import { normalizeAAPath, normalizePostMessageEvent } from './utils'

export class PostMessageParentConnection {
  private connection: ConnectionDriver | null = null
  private eventsQueue: InboxMessage[] = [{ event_name: PM_EVENT.LOADED }]
  public events = createMitt<InboxMessage>()

  private sendQueuedEvents = () => {
    this.eventsQueue.forEach((event) => {
      this.sendEvent(event)
    })
  }

  sendEvent = ({ event_name, event_data }: InboxMessage) => {
    if (!this.connection) {
      this.eventsQueue.push({ event_name, event_data })
      return
    }

    this.connection.call('handle', {
      plugin_id: PLUGIN_ID,
      message: event_name,
      data: event_data || null,
    })
  }

  handleAgentAppMessages = (message: InboxMessage) => {
    const { event_name, event_data } = normalizePostMessageEvent(message)

    switch (event_name) {
      case PM_EVENT.NAVIGATE: {
        const { pathname, replace = false } = event_data
        const normalizedPathname = normalizeAAPath(pathname)

        navigate(normalizedPathname, {
          replace:
            // Prevent doubled entry in browser history
            (canUseWindow &&
              window.location.pathname.replace(trailingSlash, '') ===
                normalizedPathname) ||
            replace,
        })
        break
      }

      case PM_EVENT.PRELOAD: {
        const pathname = normalizeAAPath(event_data.pathname)
        // eslint-disable-next-line
        // @ts-ignore
        window?.___loader.loadPage(pathname)
        break
      }

      case PM_EVENT.REFRESH_INSTALLED_APPS: {
        store?.dispatch(fetchInstalledApps.pending())
        break
      }

      case PM_EVENT.CART_UPDATED: {
        store?.dispatch(saveCart(event_data.cart))
        break
      }

      case PM_EVENT.REFRESH_CART: {
        store?.dispatch(fetchCart.pending())
        break
      }

      case PM_EVENT.REFRESH_SUBSCRIPTION_INFO: {
        store?.dispatch(fetchSubscription.pending({ omitCache: true }))
        break
      }

      default:
        break
    }

    this.events.emit(event_name, event_data)
  }

  async init() {
    if (isInIframe() && !this.connection) {
      this.connection = await connectToParent({
        handle: this.handleAgentAppMessages,
      }).promise
      this.sendQueuedEvents()
    }
  }
}
